import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CreditCard, Loader2, LogOut, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const Card = ({ title, children, tone = "white", testid }) => (
  <section
    data-testid={testid}
    className={`mt-4 rounded-lg border p-5 ${
      tone === "danger" ? "border-red-200 bg-red-50/60" : "border-slate-200 bg-white"
    }`}
  >
    <h2 className="text-xl font-semibold text-slate-800">{title}</h2>
    {children}
  </section>
);

export default function Settings() {
  const navigate = useNavigate();
  const { user, company, refreshCompany, logout } = useAuth();
  const [state, setState] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [closeOpen, setCloseOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const admin = user?.role === "company_admin";
  const readOnly = !!company?.demo_readonly;

  useEffect(() => {
    api.get("/company").then(({ data }) => setState(data)).catch(() => {});
  }, []);

  const cancelSubscription = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/company/cancel");
      setState(data);
      await refreshCompany?.();
      toast.success("Abonnemanget är uppsagt – kontot och all data ligger kvar");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const closeAccount = async () => {
    setBusy(true);
    try {
      await api.delete("/company", { data: { confirm } });
      toast.success("Företagskontot är avslutat och datan raderad");
      logout();
      navigate("/logga-in", { replace: true });
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  const leaveAccount = async () => {
    setBusy(true);
    try {
      await api.delete("/users/me");
      toast.success("Ditt användarkonto är borttaget");
      logout();
      navigate("/logga-in", { replace: true });
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  if (!state) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="mx-auto max-w-2xl" data-testid="settings-page">
      <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">Inställningar</h1>
      <p className="text-sm text-slate-500">Konto, abonnemang och avslut</p>

      <Card title="Konto" testid="settings-account-card">
        <dl className="mt-2 space-y-1 text-sm text-slate-700">
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">Företag</dt>
            <dd className="font-medium" data-testid="settings-company-name">{state.name}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">Abonnemang</dt>
            <dd className="font-medium" data-testid="settings-plan">
              {state.billing_exempt ? "Internt konto – full åtkomst" : state.plan_name || "Inget valt"}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">Användare</dt>
            <dd className="font-medium" data-testid="settings-users">
              {state.unlimited_users ? `${state.users} (obegränsat)` : `${state.users} av ${state.max_users || 0}`}
            </dd>
          </div>
        </dl>
        {state.billing_exempt && (
          <p className="mt-3 flex items-start gap-2 rounded-md border border-teal-200 bg-teal-50 p-3 text-sm font-medium text-teal-900" data-testid="settings-internal-badge">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
            Internt Nordspår-konto: permanent full tillgång utan provperiod, betalplan eller användartak.
          </p>
        )}
      </Card>

      {!state.billing_exempt && (
        <Card title="Säg upp abonnemang" testid="settings-cancel-card">
          <p className="mt-1 text-sm text-slate-600">
            Stoppar framtida betalningar men <b>behåller företagets konto och all data</b>. Du har tillgång
            till slutet av den betalda perioden och kan teckna abonnemang igen när du vill.
          </p>
          {!state.has_subscription ? (
            <p className="mt-3 text-sm font-medium text-slate-600" data-testid="settings-no-subscription">
              Du har inget aktivt abonnemang att säga upp
              {state.trial_status === "trial_active" ? " – du använder din kostnadsfria provperiod." : "."}
            </p>
          ) : state.cancel_at_period_end ? (
            <p className="mt-3 text-sm font-semibold text-amber-800" data-testid="settings-cancel-done">
              Abonnemanget är uppsagt{state.current_period_end ? ` och upphör ${state.current_period_end.slice(0, 10)}` : ""}.
            </p>
          ) : (
            <Button
              variant="outline"
              data-testid="settings-cancel-subscription"
              disabled={busy || !admin || readOnly}
              onClick={cancelSubscription}
              className="mt-3 h-10 border-amber-300 bg-white text-amber-800 hover:bg-amber-50"
            >
              <CreditCard className="mr-2 h-4 w-4" /> Säg upp abonnemang
            </Button>
          )}
          {!admin && <p className="mt-2 text-xs text-slate-500">Endast företagsadmin kan säga upp abonnemanget.</p>}
        </Card>
      )}

      <Card title="Lämna Fältmästare" testid="settings-leave-card">
        <p className="mt-1 text-sm text-slate-600">
          Raderar <b>ditt personliga användarkonto</b>. Företagets konto, arbetsordrar och övrig data
          påverkas inte. Är du enda företagsadmin måste du först utse en ny administratör.
        </p>
        <Button
          variant="outline"
          data-testid="settings-leave-account"
          disabled={busy || readOnly}
          onClick={() => setLeaveOpen(true)}
          className="mt-3 h-10 bg-white"
        >
          <LogOut className="mr-2 h-4 w-4" /> Radera mitt användarkonto
        </Button>
      </Card>

      {!state.billing_exempt && (
        <Card title="Avsluta konto" tone="danger" testid="settings-close-card">
          <p className="mt-1 text-sm text-slate-700">
            Avslutar <b>hela företagets konto</b>. Eventuellt abonnemang avslutas så att inga framtida
            debiteringar sker, och företagets data raderas permanent: användare, arbetsordrar,
            nymonteringar, tider, kalender, frånvaro, material, bilder, filer, kunduppgifter,
            faktureringsstatus och inställningar. Åtgärden går inte att ångra.
          </p>
          <p className="mt-2 flex items-start gap-2 text-sm font-medium text-red-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Vill du bara stoppa betalningarna? Använd <b>Säg upp abonnemang</b> ovan – då behålls kontot och all data.</span>
          </p>
          {admin ? (
            <Button
              data-testid="settings-close-account"
              disabled={busy || readOnly}
              onClick={() => { setConfirm(""); setCloseOpen(true); }}
              className="mt-3 h-11 bg-red-600 text-white hover:bg-red-700"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Avsluta konto
            </Button>
          ) : (
            <p className="mt-3 text-sm font-medium text-slate-600" data-testid="settings-close-denied">
              Endast företagsadmin/ägaren kan avsluta företagets konto.
            </p>
          )}
        </Card>
      )}

      <AlertDialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <AlertDialogContent className="bg-white" data-testid="leave-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Radera ditt användarkonto?</AlertDialogTitle>
            <AlertDialogDescription>
              Du loggas ut och kontot tas bort. Företagets konto och data påverkas inte.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="leave-account-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction
              data-testid="leave-account-ok"
              disabled={busy}
              onClick={(e) => { e.preventDefault(); leaveAccount(); }}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Ja, radera mitt konto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={closeOpen} onOpenChange={setCloseOpen}>
        <AlertDialogContent className="bg-white" data-testid="close-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Är du säker på att du vill avsluta företagets konto?</AlertDialogTitle>
            <AlertDialogDescription>
              Detta avslutar företagets användning av Fältmästare och företagets data kommer att raderas
              permanent. Åtgärden går inte att ångra efter att raderingen genomförts.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <Label className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">
              Skriv AVSLUTA för att bekräfta
            </Label>
            <Input
              data-testid="close-account-confirm-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="AVSLUTA"
              className="mt-1 h-10"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="close-account-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction
              data-testid="close-account-ok"
              disabled={busy || confirm.trim().toUpperCase() !== "AVSLUTA"}
              onClick={(e) => { e.preventDefault(); closeAccount(); }}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Avsluta konto permanent"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
